import type { CSSProperties } from "react";
import { Check, CircleX } from "lucide-react";

import { Mark } from "@/components/services/shared/demo-parts";

import { getAutoFlowIcon } from "./auto-flow-icons";
import type { Flow, FlowActor, FlowStep } from "./ai-automation-demo-copy";

function FlowStepMark({ logo, badge, icon }: Pick<FlowStep, "logo" | "badge" | "icon">) {
  if (logo || badge) {
    return <Mark logo={logo} badge={badge} />;
  }
  if (icon) {
    const Icon = getAutoFlowIcon(icon);
    return (
      <Icon
        className="cc-demo-mark cc-demo-mark--lucide"
        size={16}
        strokeWidth={1.5}
        aria-hidden="true"
      />
    );
  }
  return null;
}

export type CcAutoFlowStateVariant = "before" | "after";

type CcAutoFlowProps = {
  /** The flow's name and mark, shown in the top bar. */
  name: string;
  logo?: string;
  badge?: string;
  /** "Before" / "After", read out first. */
  stateLabel: string;
  /** Pill styling + icon in the flow header (auto showcase only). */
  stateVariant?: CcAutoFlowStateVariant;
  flow: Flow;
  who: Record<FlowActor, string>;
  totalLabel: string;
};

/**
 * One work flow as a numbered list of steps: who does each step (person, AI
 * or automatic), the tool involved and how long it takes, with the total
 * time per week at the bottom. One layer of the before/after.
 */
export function CcAutoFlow({
  name,
  logo,
  badge,
  stateLabel,
  stateVariant,
  flow,
  who,
  totalLabel,
}: CcAutoFlowProps) {
  const StateIcon = stateVariant === "before" ? CircleX : stateVariant === "after" ? Check : null;

  return (
    <div className="cc-auto-flow">
      <div className="cc-auto-flow__bar">
        <span className="cc-auto-flow__name">
          <Mark logo={logo} badge={badge} />
          {name}
        </span>
        <span
          className={
            stateVariant
              ? `cc-auto-flow__state cc-auto-flow__state--${stateVariant}`
              : "cc-auto-flow__state"
          }
        >
          {StateIcon ? (
            <StateIcon className="cc-auto-flow__state-icon" size={11} strokeWidth={2.25} aria-hidden />
          ) : null}
          {stateLabel}
        </span>
      </div>

      <ol
        className="cc-auto-flow__steps"
        style={{ "--cc-auto-flow-step-count": flow.steps.length } as CSSProperties}
      >
        {flow.steps.map((step, index) => (
          <li
            key={`${index}-${step.label}`}
            className="cc-auto-flow__step"
            style={{ "--cc-auto-flow-stagger": index } as CSSProperties}
          >
            <span className="cc-auto-flow__index" aria-hidden="true">
              {index + 1}
            </span>
            <span className="cc-auto-flow__label">
              {step.label}
              <span className={`cc-auto-flow__who cc-auto-flow__who--${step.who}`}>
                {who[step.who]}
              </span>
            </span>
            <span className="cc-auto-flow__meta">
              <span className="cc-auto-flow__mark-slot">
                <FlowStepMark logo={step.logo} badge={step.badge} icon={step.icon} />
              </span>
              <span className="cc-auto-flow__time" aria-hidden={!step.time}>
                {step.time ?? ""}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <p className="cc-auto-flow__total">
        <span>{totalLabel}</span>
        <strong>{flow.total}</strong>
      </p>
    </div>
  );
}
